/* ============================================================================
   CopyOverridesSyncer: loads the admin's copy overrides at launch.
   ----------------------------------------------------------------------------
   A render-null bridge, sibling of DexConfigSyncer. Installs the DOM engine
   (lib/copyOverrides) on mount, which first replays the localStorage copy so
   a cold start paints the edited text before the network answers, then
   fetches GET /api/copy-overrides (public, Cache-Control max-age=60) and
   replaces the set. Refetches on focus so an edit made on the web reaches a
   phone the next time it comes to the foreground.
============================================================================ */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { initCopyOverrides, setCopyOverrides, type CopyOverride } from "@/lib/copyOverrides";

const COPY_OVERRIDES_URL = "/api/copy-overrides";

interface CopyOverridesPayload {
  schemaVersion: number;
  generatedAt: number;
  overrides: CopyOverride[];
}

export function useCopyOverridesSync(): void {
  useEffect(() => {
    initCopyOverrides();
  }, []);
  const query = useQuery<CopyOverridesPayload>({
    queryKey: [COPY_OVERRIDES_URL],
    staleTime: 60 * 1000,
    gcTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const res = await apiRequest("GET", COPY_OVERRIDES_URL);
      return res.json();
    },
  });
  const data = query.data;
  useEffect(() => {
    if (!data || !Array.isArray(data.overrides)) return;
    setCopyOverrides(data.overrides);
  }, [data]);
}

export function CopyOverridesSyncer() {
  useCopyOverridesSync();
  return null;
}
